import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import ListingImageGallery from './ListingImageGallery/ListingImageGallery';
import BackButton from '../../components/Button/BackButton';
import TimestampBadge from '../../components/TimestampBadge/TimestampBadge';
import { getMediaPlaybackInfo } from '../../util/media';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ReactComponent as MenuReportIcon } from '../../assets/svg/MenuReportIcon.svg';
import css from './ListingPage.module.css';

const SectionGallery = props => {
  const intl = useIntl();
  const {
    listing,
    variantPrefix,
    pageType,
    handelWishlist,
    like,
    isInitialLiked,
    likeStatusLoaded = true,
    onManageDisableScrolling,
    onContactUser,
    isOwnListing,
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const menuRef = useRef(null);

  const videos = listing?.attributes?.publicData?.videos || [];
  const media = getMediaPlaybackInfo(listing?.attributes?.publicData);
  const resolvedVideos = media?.playbackUrl ? [media.playbackUrl] : videos;
  const videoItems = Array.isArray(resolvedVideos)
    ? resolvedVideos.filter(v => typeof v === 'string' && !v.toLowerCase().endsWith('.webp'))
    : [];
  const isCurrentSlideVideo = activeSlideIndex < videoItems.length && videoItems.length > 0;
  const images = listing?.images || [];
  const imageVariants = ['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge'];
  const thumbnailVariants = [variantPrefix, `${variantPrefix}-2x`, `${variantPrefix}-4x`];

  const rawLikes =
    listing?.attributes?.metadata?.likesCount ?? listing?.attributes?.publicData?.likesCount ?? 0;
  const baseLikes = typeof rawLikes === 'number' ? rawLikes : Number(rawLikes) || 0;
  const initialLikedBool = Boolean(isInitialLiked);
  const displayLikesCount = Math.max(
    0,
    baseLikes + (like ? (initialLikedBool ? 0 : 1) : initialLikedBool ? -1 : 0)
  );

  const handleCopyLink = async e => {
    e.stopPropagation();
    try {
      if (typeof window !== 'undefined') {
        await navigator.clipboard.writeText(window.location.href);
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
          setMenuOpen(false);
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShareListing = async e => {
    e.stopPropagation();
    setMenuOpen(false);
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: listing?.attributes?.title || 'Kloset Klub',
          url: typeof window !== 'undefined' ? window.location.href : '',
        });
      } catch (err) {
        console.error('Share cancelled/failed:', err);
      }
    } else {
      handleCopyLink(e);
    }
  };

  const handleReportListing = e => {
    e.stopPropagation();
    setMenuOpen(false);
    const listingId = listing?.id?.uuid || '';
    const listingTitle = listing?.attributes?.title || '';
    const listingUrl = typeof window !== 'undefined' ? window.location.href : '';
    const sellerName = listing?.author?.attributes?.profile?.displayName;
    const brand =
      listing?.attributes?.publicData?.brand || listing?.attributes?.publicData?.brandName;
    const category =
      listing?.attributes?.publicData?.categoryLevel1 || listing?.attributes?.publicData?.category;

    const lines = [
      intl.formatMessage({ id: 'ListingPage.reportListingEmailGreeting' }),
      '',
      intl.formatMessage({ id: 'ListingPage.reportListingEmailIntro' }),
      '',
      intl.formatMessage({ id: 'ListingPage.reportListingEmailListingId' }, { listingId }),
      intl.formatMessage({ id: 'ListingPage.reportListingEmailListingName' }, { listingTitle }),
      intl.formatMessage({ id: 'ListingPage.reportListingEmailListingUrl' }, { listingUrl }),
    ];

    if (sellerName) {
      lines.push(
        intl.formatMessage({ id: 'ListingPage.reportListingEmailSeller' }, { sellerName })
      );
    }
    if (brand) {
      lines.push(intl.formatMessage({ id: 'ListingPage.reportListingEmailBrand' }, { brand }));
    }
    if (category) {
      lines.push(
        intl.formatMessage({ id: 'ListingPage.reportListingEmailCategory' }, { category })
      );
    }

    lines.push('');
    lines.push(intl.formatMessage({ id: 'ListingPage.reportListingEmailReason' }));
    lines.push('');

    const subject = intl.formatMessage(
      { id: 'ListingPage.reportListingEmailSubject' },
      { listingTitle, listingId }
    );
    const body = lines.join('\n');

    if (typeof window !== 'undefined') {
      window.location.href = `mailto:care@klosetklub.com?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;
    }
  };

  if (!images || !videos) return <div>Loading...</div>;

  const mobileBottomSheet =
    menuOpen && typeof document !== 'undefined'
      ? ReactDOM.createPortal(
          <div
            className={css.mobileSheetBackdrop}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen(false);
            }}
            onTouchEnd={e => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen(false);
            }}
          >
            <div
              className={css.mobileSheetContent}
              onClick={e => e.stopPropagation()}
              onTouchEnd={e => e.stopPropagation()}
            >
              <div className={css.mobileSheetHandle} />
              <button type="button" className={css.mobileSheetItem} onClick={handleShareListing}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                <span>
                  <FormattedMessage id="ListingPage.shareListing" />
                </span>
              </button>
              <button type="button" className={css.mobileSheetItem} onClick={handleCopyLink}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                <span>
                  {isCopied ? (
                    <FormattedMessage id="ListingPage.copiedLinkToClipboard" />
                  ) : (
                    <FormattedMessage id="ListingPage.copyLink" />
                  )}
                </span>
              </button>
              <button
                type="button"
                className={classNames(css.mobileSheetItem, css.reportItem)}
                onClick={handleReportListing}
              >
                <MenuReportIcon width="18" height="18" color="currentColor" />
                <span>
                  <FormattedMessage id="ListingPage.reportListing" />
                </span>
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <section className={css.productGallery} data-testid="carousel">
      <ListingImageGallery
        images={images}
        videos={resolvedVideos}
        videoThumbnailUrl={media?.thumbnailUrl}
        imageVariants={imageVariants}
        thumbnailVariants={thumbnailVariants}
        pageType={pageType}
        onManageDisableScrolling={onManageDisableScrolling}
        isMuted={isMuted}
        onSlideChange={index => setActiveSlideIndex(index)}
        isMenuOpen={menuOpen}
      />

      {/* Top-left: Back button on mobile & Timestamp badge */}
      <div className={css.topLeftOverlay}>
        <div className={css.backButtonWrapper}>
          <BackButton className={css.galleryBackBtn} />
        </div>
        <TimestampBadge listing={listing} variant="page" />
      </div>

      {/* Top-right: Three dots menu and Audio/Speaker button */}
      <div className={css.topRightOverlay} ref={menuRef}>
        <button
          type="button"
          className={classNames(css.circleOverlayBtn, css.moreOption)}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setMenuOpen(prev => !prev);
          }}
          aria-label="More options"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </button>

        {/* Desktop Dropdown Menu & Backdrop */}
        {menuOpen && (
          <>
            <div
              className={css.desktopOverlayBackdrop}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen(false);
              }}
            />
            <div className={css.desktopShareMenu}>
              <button type="button" className={css.menuOptionItem} onClick={handleShareListing}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                <span>
                  <FormattedMessage id="ListingPage.shareListing" />
                </span>
              </button>
              <button type="button" className={css.menuOptionItem} onClick={handleCopyLink}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                <span>
                  {isCopied ? (
                    <FormattedMessage id="ListingPage.copiedLink" />
                  ) : (
                    <FormattedMessage id="ListingPage.copyLink" />
                  )}
                </span>
              </button>
              <button
                type="button"
                className={classNames(css.menuOptionItem, css.reportItem)}
                onClick={handleReportListing}
              >
                <MenuReportIcon width="16" height="16" color="currentColor" />
                <span>
                  <FormattedMessage id="ListingPage.reportListing" />
                </span>
              </button>
            </div>
          </>
        )}

        {/* Speaker / Mute Toggle Button (Only displayed for video slides) */}
        {isCurrentSlideVideo && (
          <button
            type="button"
            className={css.circleOverlayBtn}
            onClick={() => setIsMuted(prev => !prev)}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                <line x1="16" y1="10" x2="20" y2="14" />
                <line x1="20" y1="10" x2="16" y2="14" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Bottom-right: Heart like count pill */}
      <button
        type="button"
        className={classNames(css.heartCountPill, {
          [css.heartCountPillLoading]: !likeStatusLoaded,
        })}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (handelWishlist) {
            handelWishlist(e);
          }
        }}
        disabled={!likeStatusLoaded}
        aria-label={like ? 'Unlike listing' : 'Like listing'}
      >
        <svg
          className={classNames(css.heartPillIcon, {
            [css.heartPillIconLiked]: likeStatusLoaded && like,
          })}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill={likeStatusLoaded && like ? '#fec000' : 'none'}
          stroke="#fec000"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
        {likeStatusLoaded ? <span className={css.heartPillCount}>{displayLikesCount}</span> : null}
      </button>

      {mobileBottomSheet}
    </section>
  );
};

export default SectionGallery;
